import React, { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { useAuth } from '../../contexts/AuthContext';
import { useActiveCompetition } from '../../hooks/useActiveCompetition';
import GuestCatchSubmission from './GuestCatchSubmission';
import { formatClubDate } from '../../utils/clubTime';

const client = generateClient({ authMode: 'apiKey' });

/**
 * Landing page for the single, competition-agnostic catch-submission QR
 * (printed on the check-in banner and brag mat). Resolves the open
 * competition automatically — no picker in the normal case — then forks to
 * the existing member or guest catch form, exactly as SmartCheckInPage
 * forks for check-in.
 *
 * Unlike SmartCheckInPage, this page always shows the resolved competition's
 * name once known, including in the single-open (most common) case — see
 * requirements.md Requirement 2.4 for why that matters here specifically.
 */
const CatchLandingPage: React.FC = () => {
  const { user, isLoading: authLoading, isMember } = useAuth();
  const { loading, openComps, activeEventId, nextComp, setActiveEventId } = useActiveCompetition({ purpose: 'catch' });
  const [activeCompTitle, setActiveCompTitle] = useState<string | null>(null);
  const [guestMode, setGuestMode] = useState(false);

  // Once a single competition is resolved, fetch its title for display —
  // the hook only exposes the id, not the title, for the single-open case.
  useEffect(() => {
    if (!activeEventId) {
      setActiveCompTitle(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.models.Event.get({ id: activeEventId });
        if (!cancelled && data?.title) setActiveCompTitle(data.title);
      } catch (err) {
        console.error('Error loading competition title (non-fatal):', err);
      }
    })();
    return () => { cancelled = true; };
  }, [activeEventId]);

  const goDashboard = () => {
    const qs = activeEventId ? `?checkin=${activeEventId}` : '';
    window.history.pushState({}, '', `/dashboard${qs}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const goMemberSubmit = (id: string) => {
    window.history.pushState({}, '', `/competitions/${id}/submit`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  // Signed-in members skip straight to the existing member catch form —
  // no need to render anything else once we know where we're going.
  useEffect(() => {
    if (authLoading || loading || !activeEventId) return;
    if (user && isMember) {
      goMemberSubmit(activeEventId);
    }
  }, [authLoading, loading, user, isMember, activeEventId]);

  if (guestMode && activeEventId) {
    return <GuestCatchSubmission preScopedEventId={activeEventId} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-600 to-blue-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
        <img src="/tumbulgum-logo.png" alt="Tumbulgum Fishing Club" className="h-16 w-auto mx-auto mb-6" />

        {(loading || authLoading) ? (
          <div className="py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-fishing-blue mx-auto" />
            <p className="text-gray-500 mt-4">Finding the open competition…</p>
          </div>
        ) : openComps.length > 1 ? (
          <div>
            <h1 className="text-xl font-bold text-gray-900 mb-4">Multiple Competitions Open</h1>
            <p className="text-sm text-gray-500 mb-4">Select which competition you're submitting a catch for:</p>
            <div className="space-y-3">
              {openComps.map((c) => (
                <button key={c.id} onClick={() => setActiveEventId(c.id)}
                  className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-fishing-blue hover:shadow-md transition-all">
                  <p className="font-semibold text-gray-900">{c.title}</p>
                  <p className="text-sm text-gray-500">{formatClubDate(c.eventDate, { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                </button>
              ))}
            </div>
          </div>
        ) : activeEventId ? (
          // Unauthenticated, single competition resolved — always show its
          // name so the user can confirm they're submitting to the right
          // competition, then offer sign-in or guest.
          <div className="space-y-4">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Submit a Catch</h1>
            <p className="text-lg font-semibold text-fishing-blue mb-2">{activeCompTitle || 'Loading competition…'}</p>
            <p className="text-sm text-gray-500 mb-4">Sign in to your member portal for the fastest submission, and to track your catches + stats.</p>

            <button
              onClick={goDashboard}
              className="w-full py-3 bg-fishing-blue text-white rounded-lg font-semibold shadow hover:bg-blue-700"
            >
              🎣 Member sign-in
            </button>

            <div className="pt-4 border-t border-gray-200 text-left">
              <button
                onClick={() => setGuestMode(true)}
                className="w-full py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
              >
                Continue as guest
              </button>
              <p className="text-xs text-gray-500 mt-2">
                For visitors, or members who haven't set up a portal account yet — identify yourself with the email you checked in with.
              </p>
            </div>
          </div>
        ) : nextComp ? (
          <div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Catch submission opens soon</h1>
            <p className="text-lg font-semibold text-fishing-blue mb-1">{nextComp.title}</p>
            <p className="text-sm text-gray-500 mb-4">
              {formatClubDate(nextComp.eventDate, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              {nextComp.location ? ` · ${nextComp.location}` : ''}
            </p>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-800">✅ You're in the right place!</p>
              <p className="text-xs text-blue-600 mt-1">Check back on competition day when catch submission opens.</p>
            </div>
          </div>
        ) : (
          <div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">No competitions scheduled</h1>
            <p className="text-sm text-gray-500">Check back soon for the next competition.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CatchLandingPage;
